/* istanbul ignore file */ // not fully implemented yet
import React, { useState, useCallback, useMemo } from 'react';
import {
  Divider,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Color,
  State,
  AssetName,
  AdjustViewProps,
  TextFormattingOption,
} from './types';
import {
  CallToAction,
  TextFormattingButton,
  TextFormattingDialogue,
  TextAssetCollectionWithRecommendations,
} from './components';
import { assetName } from './constants';
import { ActionPaneComp } from './action-pane';
import {
  getImageAssetConfigs,
  getAllTextFormattingOptions,
  getTransformedAssetProperties,
} from './utils';
import { RecommendationsBanner } from '../src/components/recommendations-banner/index';

const useStyles = makeStyles({
  textCollection: {},
  textDivider: {
    ...shorthands.margin(tokens.spacingVerticalL, 0),
  },
  container: {
    backgroundColor: tokens.colorNeutralBackground1,
    maxHeight: '100%',
    ...shorthands.borderRadius(tokens.borderRadiusXLarge),
    ...shorthands.overflow('auto', 'hidden'),
    display: 'flex',
    ...shorthands.padding(
      tokens.spacingVerticalSNudge,
      tokens.spacingHorizontalSNudge
    ),
    boxShadow: tokens.shadow4,

    '& > div.adjust-content': {
      width: '320px',
      ...shorthands.overflow('hidden', 'auto'),
      ...shorthands.padding(
        tokens.spacingVerticalXXL,
        tokens.spacingHorizontalL
      ),
    },
  },
});

const getTextAssets = (
  displayAds: State['displayAds'],
  targetSizes: State['targetSizes'],
  configs: State['templateConfigs'],
  prop: 'shortHeadline' | 'longHeadline' | 'description' | 'cta'
) => {
  const textAssets: Set<any> = new Set();
  let minLength = Number.MAX_SAFE_INTEGER;
  let maxLength = 0;
  targetSizes.forEach((size) => {
    const ad = displayAds[size];
    const config = configs[size];
    if (config?.[prop]?.display) {
      textAssets.add(ad?.[prop]?.text);
      if (config[prop].minLengthInChar < minLength) {
        minLength = config[prop].minLengthInChar;
      }
      if (config[prop].maxLengthInChar > maxLength) {
        maxLength = config[prop].maxLengthInChar;
      }
    }
  });

  const isMultipleOptions = textAssets.size > 1;
  const text = Array.from(textAssets)[0];

  return {
    minLength,
    maxLength,
    isMultipleOptions,
    display: textAssets.size > 0,
    text: text ? text.slice(0, maxLength) : '',
    placeholder: isMultipleOptions
      ? _TL_('Multiple options, editing will override for all texts')
      : '',
  };
};

export const AdjustView = ({
  onBackgroundColorChange,
  onImageChange,
  targetSizes,
  onLogoChange,
  onCtaChange,
  onChangeTextAssets,
  onAssetPropertiesWithColorChange,
  recommendedAssets,
  isAssetsLoading,
  showRecommendationsBanner,
  displayAds,
  templateConfigs,
  serverContext,
  preferenceService,
  perfMarker,
  aid,
  cid,
  oDataConfig,
  i18n,
  permissions,
  onFinalUrlChange,
  onRecommendedAssetsChange,
  instrumentation,
  finalUrl,
}: AdjustViewProps) => {
  const classes = useStyles();
  const [
    activeTextFormattingSection,
    setActiveTextFormattingSection,
  ] = useState<AssetName | null>();

  const handleOnFetchSucceeded = useCallback(
    (result) => {
      onRecommendedAssetsChange(result);
    },
    [onRecommendedAssetsChange]
  );

  const handleSetFinalUrl = useCallback((copilotUrl) => {
    onFinalUrlChange(copilotUrl);
  }, [onFinalUrlChange]);

  const actionPanelDisplayData = useMemo(() => {
    const {
      display: displayImage,
      apectRatioSet: imageAspectRatio,
      displayBackground,
    } = getImageAssetConfigs(templateConfigs, targetSizes, 'image');
    const {
      display: displayLogo,
      apectRatioSet: logoAspectRatio,
    } = getImageAssetConfigs(templateConfigs, targetSizes, 'logo');
    return {
      displayImage,
      displayLogo,
      displayBackground,
      imageAspectRatio,
      logoAspectRatio,
    };
  }, [targetSizes, templateConfigs]);

  const actionPanelPreviewData = useMemo(() => {
    const imageUrls: Set<any> = new Set();
    const logoUrls: Set<any> = new Set();
    const colorsMap: Map<string, any> = new Map();
    const imageData: any = [];
    const logoData: any = [];
    targetSizes.forEach((size) => {
      const ad = displayAds[size];
      if (ad && ad.image && ad.image.url) {
        imageUrls.add(ad.image.url);
        imageData.push(ad.image);
      }
      if (ad && ad.logo && ad.logo.url) {
        logoUrls.add(ad.logo.url);
        logoData.push(ad.logo);
      }
      ad?.color?.forEach(({ elementName, editable, color: colorCode }) => {
        if (editable) {
          if (colorsMap.has(elementName)) {
            colorsMap.get(elementName).add(colorCode);
          } else {
            colorsMap.set(elementName, new Set([colorCode]));
          }
        }
      });
    });
    return {
      imageUrls,
      logoUrls,
      colorsMap,
      imageData,
      logoData,
    };
  }, [displayAds, targetSizes]);
  const shortHeadline = useMemo(
    () =>
      getTextAssets(displayAds, targetSizes, templateConfigs, 'shortHeadline'),
    [displayAds, targetSizes, templateConfigs]
  );

  const longHeadline = useMemo(
    () =>
      getTextAssets(displayAds, targetSizes, templateConfigs, 'longHeadline'),
    [displayAds, targetSizes, templateConfigs]
  );

  const description = useMemo(
    () =>
      getTextAssets(displayAds, targetSizes, templateConfigs, 'description'),
    [displayAds, targetSizes, templateConfigs]
  );

  const callToAction = useMemo(
    () => getTextAssets(displayAds, targetSizes, templateConfigs, 'cta'),
    [displayAds, targetSizes, templateConfigs]
  );

  const textFormattingOptions = useMemo(
    () => getAllTextFormattingOptions(displayAds, targetSizes),
    [displayAds, targetSizes]
  );

  const handleChangeSectionAssetFormatting = (
    newFormatting: TextFormattingOption,
    section: AssetName
  ) => {
    const updatedAssetProperties = getTransformedAssetProperties(
      newFormatting,
      section
    );
    onAssetPropertiesWithColorChange({
      assetProperties: updatedAssetProperties,
      color: {
        color: newFormatting.color,
        elementName: section,
      } as Color,
    });
  };

  return (
    <aside className={mergeClasses('adjust', classes.container)}>
      {activeTextFormattingSection && (
      <div className="adjust-content" >
        <TextFormattingDialogue
          i18n={i18n}
          onToggleBack={() => setActiveTextFormattingSection(null)}
          textFormatOptions={textFormattingOptions[activeTextFormattingSection]}
          hideColorFormatting={activeTextFormattingSection === assetName.callToAction}
          onFormatText={(newFormatting: TextFormattingOption) =>
            handleChangeSectionAssetFormatting(
              newFormatting,
              activeTextFormattingSection
            )
          }
        />
      </div>
      )}
      <div
        className="adjust-content"
        style={{
          display: activeTextFormattingSection ? 'none' : 'block',
        }}
      >
        {showRecommendationsBanner &&
          <RecommendationsBanner
            i18n={i18n}
            aid={aid}
            cid={cid}
            instrumentation={instrumentation}
            permissions={permissions}
            serverContext={serverContext}
            perfMarker={perfMarker}
            finalUrl={finalUrl}
            setFinalUrl={handleSetFinalUrl}
            handleOnFetchSucceeded={handleOnFetchSucceeded}
          />
        }
        <ActionPaneComp
          onImageChange={onImageChange}
          onLogoChange={onLogoChange}
          finalUrl={finalUrl}
          isAssetsLoading={isAssetsLoading}
          showRecommendationsBanner={showRecommendationsBanner}
          recommendedAssets={recommendedAssets}
          onBackgroundColorChange={onBackgroundColorChange}
          serverContext={serverContext}
          preferenceService={preferenceService}
          perfMarker={perfMarker}
          imagesUrl={actionPanelPreviewData.imageUrls}
          logosUrl={actionPanelPreviewData.logoUrls}
          aid={aid}
          images={actionPanelPreviewData.imageData}
          logos={actionPanelPreviewData.logoData}
          actionPanelDisplayData={actionPanelDisplayData}
          cid={cid}
          oDataConfig={oDataConfig}
          i18n={i18n}
          permissions={permissions}
          backgroundColors={actionPanelPreviewData.colorsMap.get('Background')}
        />
        <div
          className={mergeClasses(
            'adjust-text-collection',
            classes.textCollection
          )}
        >
          <TextAssetCollectionWithRecommendations
            url={finalUrl}
            disableCopilot={!finalUrl && !showRecommendationsBanner}
            i18n={i18n}
            campaignId={cid}
            serverContext={serverContext}
            onChangeTexts={onChangeTextAssets}
            onChangeTextAssetFormattingSection={setActiveTextFormattingSection}
            shortHeadline={shortHeadline.text}
            longHeadline={longHeadline.text}
            description={description.text}
            shortHeadlineValidations={shortHeadline}
            longHeadlineValidations={longHeadline}
            descriptionValidations={description}
          />
          {callToAction.display && (
            <>
              <Divider appearance="strong" className={classes.textDivider} />
              <CallToAction
                i18n={i18n}
                cta={callToAction?.text}
                onChangeCta={onCtaChange}
                ctaValidations={callToAction}
                label={i18n.getString(_TL_('Call to action'))}
                helpLabel={i18n.getString(_TL_('Click to learn more about call to action'))}
                helpTopic="pop_BA_AudAds_CTA"
                placeholder={
                  callToAction.isMultipleOptions
                    ? i18n.getString(_TL_('Multiple options, editing will override for all texts'))
                    : i18n.getString(_TL_('Select call to action'))
                }
                labelAction={
                  <TextFormattingButton
                    i18n={i18n}
                    onClick={() =>
                      setActiveTextFormattingSection(assetName.callToAction)
                    }
                  />
                }
              />
            </>
          )}
        </div>
      </div>
    </aside>
  );
};
